import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../utils/supabase";
import AuthLayout from "./AuthLayout";

function PricingPage() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadUser() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const currentUser = session?.user;
        if (currentUser) {
          setUser(currentUser);
          const { data } = await supabase
            .from("profiles")
            .select("subscription_tier")
            .eq("id", currentUser.id)
            .single();
          if (data) setProfile(data);
        }
      } catch (error) {
        console.error("Error loading user:", error);
      } finally {
        setLoading(false);
      }
    }
    loadUser();
  }, []);

  const handleUpgrade = async () => {
    if (!user) {
      navigate("/login");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/create-checkout-session`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            user_id: user.id,
            email: user.email,
          }),
        },
      );
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert("Something went wrong. please try again.");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Failed to start checkout. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isPro = profile?.subscription_tier === "pro";

  return (
    <AuthLayout>
      <div className="w-full max-w-[700px]">
        <h1
          className="text-text-primary text-[28px] mb-2 text-center"
          style={{ fontWeight: 700 }}
        >
          Pricing
        </h1>
        <p
          className="text-[#b0b0b0] text-[13px] mb-10 text-center"
          style={{ fontWeight: 500 }}
        >
          Choose your plan
        </p>

        {/* Card container */}
        <div className="flex flex-col md:flex-row gap-6">
          {/* Free Plan */}
          <div
            className="flex-1 border border-[#222] p-6"
            style={{ backgroundColor: "#0a0a0a" }}
          >
            <h3
              className="text-[#888] text-[10px] uppercase tracking-[3px] mb-4"
              style={{ fontWeight: 600 }}
            >
              Hobby
            </h3>
            <div className="flex items-baseline gap-1 mb-1">
              <span
                className="text-text-primary text-[32px]"
                style={{ fontWeight: 700 }}
              >
                $0
              </span>
              <span className="text-[#888] text-[14px]">/month</span>
            </div>
            <p
              className="text-[#b0b0b0] text-[12px] mb-6"
              style={{ fontWeight: 500 }}
            >
              Get started for free
            </p>

            <div className="border-t border-[#222] pt-4 mb-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[#FF6B00] text-[12px]">→</span>
                  <span className="text-[#b0b0b0] text-[13px]">
                    2 videos per month
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#FF6B00] text-[12px]">→</span>
                  <span className="text-[#b0b0b0] text-[13px]">
                    30 min max video length
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#FF6B00] text-[12px]">→</span>
                  <span className="text-[#b0b0b0] text-[13px]">
                    Basic AI highlights
                  </span>
                </div>
              </div>
            </div>

            <button
              disabled
              className="w-full text-[10px] uppercase tracking-[3px] py-3 border border-[#333] text-[#888]"
              style={{ fontWeight: 600, cursor: "default" }}
            >
              {isPro ? "Free Plan" : "Current Plan"}
            </button>
          </div>

          {/* Pro Plan */}
          <div
            className="flex-1 border border-[#222] p-6"
            style={{
              backgroundColor: "#0a0a0a",
              borderTop: "2px solid #FF6B00",
            }}
          >
            <h3
              className="text-[#888] text-[10px] uppercase tracking-[3px] mb-4"
              style={{ fontWeight: 600 }}
            >
              Pro
            </h3>
            <div className="flex items-baseline gap-1 mb-1">
              <span
                className="text-text-primary text-[32px]"
                style={{ fontWeight: 700 }}
              >
                $5
              </span>
              <span className="text-[#888] text-[14px]">/ month</span>
            </div>
            <p
              className="text-[#b0b0b0] text-[12px] mb-6"
              style={{ fontWeight: 500 }}
            >
              First 30 days free trial
            </p>

            <div className="border-t border-[#222] pt-4 mb-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[#FF6B00] text-[12px]">→</span>
                  <span className="text-[#b0b0b0] text-[13px]">
                    Unlimited videos
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#FF6B00] text-[12px]">→</span>
                  <span className="text-[#b0b0b0] text-[13px]">
                    Up to 2 hours video length
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#FF6B00] text-[12px]">→</span>
                  <span className="text-[#b0b0b0] text-[13px]">
                    Priority processing
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#FF6B00] text-[12px]">→</span>
                  <span className="text-[#b0b0b0] text-[13px]">
                    Access to new features
                  </span>
                </div>
              </div>
            </div>

            {isPro ? (
              <button
                disabled
                className="w-full text-[10px] uppercase tracking-[3px] py-3 border border-[#333] text-[#888]"
                style={{ fontWeight: 700, cursor: "default" }}
              >
                Current Plan
              </button>
            ) : (
              <button
                onClick={handleUpgrade}
                disabled={loading}
                className="w-full text-[10px] uppercase tracking-[3px] py-3 transition-all duration-200"
                style={{
                  backgroundColor: "#FF6B00",
                  color: "#000",
                  fontWeight: 700,
                  opacity: loading ? 0.6 : 1,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Loading..." : "Upgrade to Pro"}
              </button>
            )}
          </div>
        </div>

        {/* Footer text */}
        <p
          className="text-[#777] text-[11px] text-center mt-8"
          style={{ fontWeight: 500 }}
        >
          Cancel anytime. No hidden fees.
        </p>
      </div>
    </AuthLayout>
  );
}

export default PricingPage;
